// Online payments' server state (spec/06 §9.3, F-PAY-03), as TanStack Query hooks over the generated
// client — `packages/contracts/src/routes/payments.ts`:
//
//   GET  …/venues/{v}/payments/online                           are online payments open here    orders:read
//   POST …/venues/{v}/sales/{id}/payment-intents                ask the player to pay online     orders:create
//   GET  …/venues/{v}/payment-intents/{id}                      poll it                          orders:read
//   POST …/venues/{v}/payment-intents/{id}/cancel               abandon it                       orders:create
//   POST …/players/{p}/wallet/online-top-ups                    a wallet top-up paid online      wallet:topup
//
// The console never tells the server a payment succeeded: only the payment provider does (spec/06
// §9.3). So an intent is polled until the provider's answer — captured, failed, cancelled or expired —
// is recorded, and the sale or the wallet is re-read then.

import type { ApiClient, Money, OnlinePaymentsStatus, PaymentIntent } from '@clavian/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApi } from '../../api/client';

/** How often an open intent is asked about while the player pays. */
export const PAYMENT_POLL_MS = 2000;

export const paymentKeys = {
  online: (organizationId: string, venueId: string) => ['payments', organizationId, venueId, 'online'] as const,
  intent: (organizationId: string, venueId: string, intentId: string) => ['payments', organizationId, venueId, 'intent', intentId] as const,
};

/** An intent the provider has answered: nothing more will change it. */
export function isSettled(intent: PaymentIntent): boolean {
  return intent.status !== 'pending';
}

/** Whether the venue's gates are open (spec/06 §9.5) — the online tender is offered only then. */
export function useOnlinePayments(organizationId: string, venueId: string) {
  const api = useApi();
  return useQuery<OnlinePaymentsStatus>({
    queryKey: paymentKeys.online(organizationId, venueId),
    queryFn: () => api.getOnlinePaymentsStatus({ path: { organizationId, venueId } }),
    staleTime: 60_000,
  });
}

/** Ask the player to pay a sale, or part of it, online. A fresh `Idempotency-Key` per request. */
export function useCreateSalePaymentIntent(organizationId: string, venueId: string) {
  const api = useApi();
  return useMutation({
    mutationFn: ({ saleId, amount }: { saleId: string; amount?: Money }) =>
      api.createSalePaymentIntent({
        path: { organizationId, venueId, saleId },
        headerParams: { 'idempotency-key': crypto.randomUUID() },
        body: amount === undefined ? {} : { amount },
      }),
  });
}

/** Begin an online wallet top-up at this venue (F-WAL-02): credited only when the provider confirms it. */
export function useBeginOnlineTopUp(organizationId: string, venueId: string) {
  const api = useApi();
  return useMutation({
    mutationFn: ({ playerId, amount }: { playerId: string; amount: Money }) => beginOnlineTopUp(api, organizationId, venueId, playerId, amount),
  });
}

function beginOnlineTopUp(api: ApiClient, organizationId: string, venueId: string, playerId: string, amount: Money) {
  return api.beginOnlineWalletTopUp({
    path: { organizationId, playerId },
    headerParams: { 'idempotency-key': crypto.randomUUID() },
    // The wallet is org-wide, so the venue the top-up is begun at is a header, not the path.
    headers: { 'X-Clavian-Venue': venueId },
    body: { amount },
  });
}

/** An intent, polled every couple of seconds until the provider has answered it. */
export function usePaymentIntent(organizationId: string, venueId: string, initial: PaymentIntent) {
  const api = useApi();
  return useQuery<PaymentIntent>({
    queryKey: paymentKeys.intent(organizationId, venueId, initial.id),
    queryFn: () => api.getPaymentIntent({ path: { organizationId, venueId, paymentIntentId: initial.id } }),
    initialData: initial,
    refetchInterval: (query) => (query.state.data !== undefined && isSettled(query.state.data) ? false : PAYMENT_POLL_MS),
  });
}

/** Abandon an open intent; the answer replaces the polled one. */
export function useCancelPaymentIntent(organizationId: string, venueId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (intentId: string) => api.cancelPaymentIntent({ path: { organizationId, venueId, paymentIntentId: intentId } }),
    onSuccess: (intent) => queryClient.setQueryData(paymentKeys.intent(organizationId, venueId, intent.id), intent),
  });
}
